import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Form, notification, Row, Col, Button, Modal, Typography, Upload, Spin, Tooltip, Divider } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import * as userActions from '../../redux/actions/userActions';
import config from '../../config';
import {
  confirmationModal,
  formItemLayout,
  tailFormItemLayout,
  unexpectedError,
  showFilesList,
  showUploadFileList,
  beforeUpload,
} from '../../common_functions';
import UploadTrigger from '../../common_functions/UploadTrigger';

const PhotoSpectro = props => {
  const dispatch = useDispatch();
  const formRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [photoData, setPhotoData] = useState(false);
  const [title, setTitle] = useState('Add Glossmeter');
  const [printData, setPrintData] = useState(false);
  const [filesState, setFilesState] = useState({
    dmax_oba: [],
    dmax_lab: [],
    dmax_spectrum: [],
    dmin_oba: [],
    dmin_lab: [],
    dmin_spectrum: [],
    light_gray_oba: [],
    light_gray_lab: [],
    light_gray_spectrum: [],
    medium_gray_oba: [],
    medium_gray_lab: [],
    medium_gray_spectrum: [],
  });
  let error = [];
  let saveAndNext = '';

  useEffect(() => {
    setLoading(true);
    async function fetchData() {
      try {
        const { data } = await dispatch(
          userActions.getItem('print', props.print, {
            fields: ['*.*'],
            fields: ['c_print.acquisition,c_print.title,collection_id.name,photo_spectro_meter.*,glossmeter.id'],
          })
        );
        const acquisitionID = data?.c_print?.[0]?.['acquisition'];
        setPrintData({
          acquisition: acquisitionID,
          title: data.c_print[0]['title'],
          name: data.collection_id.name,
        });

        const { photo_spectro_meter: photoSpectroData, glossmeter } = data;

        if (photoSpectroData?.[0]) {
          const photo_spectro_meter = photoSpectroData[0];
          setFilesState({
            dmax_oba: showUploadFileList(photo_spectro_meter?.dmax_oba, `${acquisitionID}.dmax.oba.csv`),
            dmax_lab: showUploadFileList(photo_spectro_meter?.dmax_lab, `${acquisitionID}.dmax.lab.txt`),
            dmax_spectrum: showUploadFileList(photo_spectro_meter?.dmax_spectrum, `${acquisitionID}.dmax.spectrum.txt`),
            dmin_oba: showUploadFileList(photo_spectro_meter?.dmin_oba, `${acquisitionID}.dmin.oba.csv`),
            dmin_lab: showUploadFileList(photo_spectro_meter?.dmin_lab, `${acquisitionID}.dmin.oba.txt`),
            dmin_spectrum: showUploadFileList(photo_spectro_meter?.dmin_spectrum, `${acquisitionID}.dmin.spectrum.txt`),
            light_gray_oba: showUploadFileList(photo_spectro_meter?.light_gray_oba, `${acquisitionID}.lg.oba.csv`),
            light_gray_lab: showUploadFileList(photo_spectro_meter?.light_gray_lab, `${acquisitionID}.lg.lab.txt`),
            light_gray_spectrum: showUploadFileList(photo_spectro_meter?.light_gray_spectrum, `${acquisitionID}.lg.spectrum.txt`),
            medium_gray_oba: showUploadFileList(photo_spectro_meter?.dmax_oba, `${acquisitionID}.mg.oba.csv`),
            medium_gray_lab: showUploadFileList(photo_spectro_meter?.medium_gray_lab, `${acquisitionID}.mg.lab.txt`),
            medium_gray_spectrum: showUploadFileList(photo_spectro_meter?.medium_gray_spectrum, `${acquisitionID}.mg.spectrum.txt`),
          });
          formRef.current?.setFieldsValue(photo_spectro_meter);
          setPhotoData(photo_spectro_meter);
        }

        if (glossmeter?.[0]) {
          setTitle('Edit Glossmeter');
        }
      } catch (error) {
        unexpectedError(error);
      }
      setLoading(false);
    }
    if (props.activeTab === 'photospectrometer') {
      fetchData();
    }
  }, [props.activeTab]);

  const complete = async values => {
    Modal.destroyAll();
    values = { ...values, object_status: 1 };
    await onSave(values);
  };
  const incomplete = async values => {
    Modal.destroyAll();
    values = { ...values, object_status: 0 };
    await onSave(values);
  };
  let onFinish = async (values, action = null) => {
    saveAndNext = '';
    if (!!action) {
      saveAndNext = action;
    }
    await confirmationModal(values, 'Status', 'Is this record complete or incomplete?', 'Incomplete', 'Complete', complete, incomplete);
  };

  let onSave = async values => {
    if (error.length) return false;
    setLoading(true);
    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      key === 'object_status' && formData.append('object_status', value);
      if (!!value && !!value.fileList && value.fileList.length > 0) {
        formData.append(key, value.fileList[0].originFileObj);
      }
    });
    dispatch(userActions.saveFileItem('photo_spectro/photo_spectro_meter', formData, props.print, photoData)).then(
      response => {
        setLoading(false);
        if (response) {
          formRef.current.resetFields('');
          formRef.current.setFieldsValue(response.data);
          setPhotoData(response.data);
          if (response.error) {
            unexpectedError(response.error);
          } else {
            notification['success']({
              message: 'Photospectrometer saved successfully!',
              duration: 2,
            });
            if (saveAndNext == '') {
              setTimeout(() => props.history.push('/print-objects', 1500));
            } else if (saveAndNext == 'next') {
              props.onChange('', 'image_desc', 'Image Description'), 1500;
            }
          }
        }
      },
      errorReponse => {
        setLoading(false);
        if (!!errorReponse.errors) {
          Object.entries(errorReponse.errors).map(function (value, index) {
            formRef.current.setFields([
              {
                name: value[0],
                errors: [value[1]],
              },
            ]);
          });
        } else {
          setLoading(false);
          unexpectedError();
        }
      }
    );
  };
  let onFinishFailed = ({ errorFields }) => {
    formRef.current.scrollToField(errorFields[0].name);
  };
  let backButton = () => {
    props.onChange('', 'glossmeter', title);
  };
  let fileChange = ({ file, fileList: newFileList }, fieldName, fileName) => {
    try {
      error = [];
      let correctFileName = `${printData.acquisition}.${fileName}`;
      if (!(file instanceof File)) return;
      if (file.name.toLowerCase() !== correctFileName.toLowerCase()) {
        error.push(`Please upload a file with the name ${correctFileName}`);
      } else {
        setFilesState(prevState => ({
          ...prevState,
          [fieldName]: newFileList,
        }));
      }
      formRef.current.setFields([
        {
          name: fieldName,
          errors: error.length ? error : '',
        },
      ]);
    } catch (error) {
      console.log(error);
    }
  };
  const onRemove = (file, fieldName) => {
    formRef.current.setFields([
      {
        name: fieldName,
        errors: '',
      },
    ]);
    return new Promise((resolve, reject) => {
      resolve(true);
    });
  };

  const handleSaveAndNext = action => {
    formRef.current.validateFields().then(values => {
      onFinish(values, action);
    });
  };

  return (
    <Spin spinning={loading} size="large">
      <Row>
        <Col className="white-paper">
          <Row
            style={{
              backgroundColor: '#f5f5f5',
              border: '1px solid #0000000f',
              borderRadius: '7px',
              padding: '12px',
            }}
          >
            <Col md={8}>
              <div>Collection</div>
              <Typography.Title level={4} style={{ margin: 0 }}>
                <div style={{ fontSize: '19px' }}>{printData.name}</div>
              </Typography.Title>
            </Col>
            <Col md={8}>
              <div>Collection Acquisition ID</div>
              <Typography.Title level={4} style={{ margin: 0 }}>
                <div style={{ fontSize: '19px' }}>{printData.acquisition}</div>
              </Typography.Title>
            </Col>
            <Col md={8}>
              <div>Collection Title</div>
              <Typography.Title level={4} style={{ margin: 0 }}>
                <div style={{ fontSize: '19px' }}>{printData.title}</div>
              </Typography.Title>
            </Col>
          </Row>
          {/* Tabs start here */}
          <div className="artist" style={{ marginTop: '20px' }}>
            <Form
              ref={formRef}
              layout="vertical"
              {...formItemLayout}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              initialValues={{ layout: tailFormItemLayout }}
              encType="multipart/form-data"
            >
              <Row>
                <Col md={24}>
                  <Typography.Title level={4} style={{ margin: 0 }}>
                    DMAX
                  </Typography.Title>
                </Col>
              </Row>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item name="dmax_oba" label="OBA">
                    <Upload
                      name="file"
                      fileList={filesState.dmax_oba}
                      accept={config.FileType.onlyCSV.toString()}
                      beforeUpload={file => {
                        return beforeUpload(file, 'onlyCSV', `${printData.acquisition}.dmax.oba`);
                      }}
                      onRemove={e => onRemove(e, 'dmax_oba')}
                      multiple={false}
                      maxCount={1}
                      onChange={e => fileChange(e, 'dmax_oba', `dmax.oba.csv`)}
                      showUploadList={showFilesList(photoData?.dmax_oba, 'dmax.oba.csv')}
                    >
                      <Row justify="space-between" align="middle">
                        <Col flex="auto">
                          <UploadTrigger fileType="onlyCSV" />
                        </Col>
                        {!!photoData && photoData.dmax_oba && (
                          <Col span={2}>
                            <Tooltip placement="rightTop" title="Uploaded">
                              <CheckOutlined
                                style={{
                                  fontSize: '250%',
                                  color: 'green',
                                }}
                              />
                            </Tooltip>
                          </Col>
                        )}
                      </Row>
                    </Upload>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item name="dmax_lab" label="LAB">
                    <Upload
                      name="file"
                      fileList={filesState.dmax_lab}
                      accept={config.FileType.txtType.toString()}
                      beforeUpload={file => {
                        return beforeUpload(file, 'txtType', `${printData.acquisition}.dmax.lab`);
                      }}
                      onRemove={e => onRemove(e, 'dmax_lab')}
                      multiple={false}
                      maxCount={1}
                      onChange={e => fileChange(e, 'dmax_lab', `dmax.lab.txt`)}
                      showUploadList={showFilesList(photoData?.dmax_lab, 'dmax.lab.txt')}
                    >
                      <Row justify="space-between" align="middle">
                        <Col flex="auto">
                          <UploadTrigger fileType="txtType" />
                        </Col>
                        {!!photoData && photoData.dmax_lab && (
                          <Col span={2}>
                            <Tooltip placement="rightTop" title="Uploaded">
                              <CheckOutlined
                                style={{
                                  fontSize: '250%',
                                  color: 'green',
                                }}
                              />
                            </Tooltip>
                          </Col>
                        )}
                      </Row>
                    </Upload>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item name="dmax_spectrum" label="Spectrum">
                    <Upload
                      name="file"
                      fileList={filesState.dmax_spectrum}
                      accept={config.FileType.txtType.toString()}
                      beforeUpload={file => {
                        return beforeUpload(file, 'txtType', `${printData.acquisition}.dmax.spectrum`);
                      }}
                      onRemove={e => onRemove(e, 'dmax_spectrum')}
                      multiple={false}
                      maxCount={1}
                      onChange={e => fileChange(e, 'dmax_spectrum', 'dmax.spectrum.txt')}
                      showUploadList={showFilesList(photoData?.dmax_spectrum, 'dmax.spetrum.txt')}
                    >
                      <Row justify="space-between" align="middle">
                        <Col flex="auto">
                          <UploadTrigger fileType="txtType" />
                        </Col>
                        {!!photoData && photoData.dmax_spectrum && (
                          <Col span={2}>
                            <Tooltip placement="rightTop" title="Uploaded">
                              <CheckOutlined
                                style={{
                                  fontSize: '250%',
                                  color: 'green',
                                }}
                              />
                            </Tooltip>
                          </Col>
                        )}
                      </Row>
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>
              <Divider />
              <Row>
                <Col md={24}>
                  <Typography.Title level={4} style={{ margin: 0 }}>
                    DMIN
                  </Typography.Title>
                </Col>
              </Row>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item name="dmin_oba" label="OBA">
                    <Upload
                      name="file"
                      fileList={filesState.dmin_oba}
                      accept={config.FileType.onlyCSV.toString()}
                      beforeUpload={file => {
                        return beforeUpload(file, 'onlyCSV', `${printData.acquisition}.dmin.oba`);
                      }}
                      onRemove={e => onRemove(e, 'dmin_oba')}
                      multiple={false}
                      maxCount={1}
                      onChange={e => fileChange(e, 'dmin_oba', `dmin.oba.csv`)}
                      showUploadList={showFilesList(photoData?.dmin_oba, 'dmin.oba.csv')}
                    >
                      <Row justify="space-between" align="middle">
                        <Col flex="auto">
                          <UploadTrigger fileType="onlyCSV" />
                        </Col>
                        {!!photoData && photoData.dmin_oba && (
                          <Col span={2}>
                            <Tooltip placement="rightTop" title="Uploaded">
                              <CheckOutlined
                                style={{
                                  fontSize: '250%',
                                  color: 'green',
                                }}
                              />
                            </Tooltip>
                          </Col>
                        )}
                      </Row>
                    </Upload>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item name="dmin_lab" label="LAB">
                    <Upload
                      name="file"
                      fileList={filesState.dmin_lab}
                      accept={config.FileType.txtType.toString()}
                      beforeUpload={file => {
                        return beforeUpload(file, 'txtType', `${printData.acquisition}.dmin.lab`);
                      }}
                      onRemove={e => onRemove(e, 'dmin_lab')}
                      multiple={false}
                      maxCount={1}
                      onChange={e => fileChange(e, 'dmin_lab', `dmin.lab.txt`)}
                      showUploadList={showFilesList(photoData?.dmin_lab, 'dmin.lab.txt')}
                    >
                      <Row justify="space-between" align="middle">
                        <Col flex="auto">
                          <UploadTrigger fileType="txtType" />
                        </Col>
                        {!!photoData && photoData.dmin_lab && (
                          <Col span={2}>
                            <Tooltip placement="rightTop" title="Uploaded">
                              <CheckOutlined
                                style={{
                                  fontSize: '250%',
                                  color: 'green',
                                }}
                              />
                            </Tooltip>
                          </Col>
                        )}
                      </Row>
                    </Upload>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item name="dmin_spectrum" label="Spectrum">
                    <Upload
                      name="file"
                      fileList={filesState.dmin_spectrum}
                      accept={config.FileType.txtType.toString()}
                      beforeUpload={file => {
                        return beforeUpload(file, 'txtType', `${printData.acquisition}.dmin.spectrum`);
                      }}
                      onRemove={e => onRemove(e, 'dmin_spectrum')}
                      multiple={false}
                      maxCount={1}
                      onChange={e => fileChange(e, 'dmin_spectrum', 'dmin.spectrum.txt')}
                      showUploadList={showFilesList(photoData?.dmin_spectrum, 'dmin.spectrum.txt')}
                    >
                      <Row justify="space-between" align="middle">
                        <Col flex="auto">
                          <UploadTrigger fileType="txtType" />
                        </Col>
                        {!!photoData && photoData.dmin_spectrum && (
                          <Col span={2}>
                            <Tooltip placement="rightTop" title="Uploaded">
                              <CheckOutlined
                                style={{
                                  fontSize: '250%',
                                  color: 'green',
                                }}
                              />
                            </Tooltip>
                          </Col>
                        )}
                      </Row>
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>
              <Divider />
              <Row>
                <Col md={24}>
                  <Typography.Title level={4} style={{ margin: 0 }}>
                    Light Gray
                  </Typography.Title>
                </Col>
              </Row>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item name="light_gray_oba" label="OBA">
                    <Upload
                      name="file"
                      fileList={filesState.light_gray_oba}
                      accept={config.FileType.onlyCSV.toString()}
                      beforeUpload={file => {
                        return beforeUpload(file, 'onlyCSV', `${printData.acquisition}.lg.oba`);
                      }}
                      onRemove={e => onRemove(e, 'light_gray_oba')}
                      multiple={false}
                      maxCount={1}
                      onChange={e => fileChange(e, 'light_gray_oba', 'lg.oba.csv')}
                      showUploadList={showFilesList(photoData?.light_gray_oba, 'lg.oba.csv')}
                    >
                      <Row justify="space-between" align="middle">
                        <Col flex="auto">
                          <UploadTrigger fileType="onlyCSV" />
                        </Col>
                        {!!photoData && photoData.light_gray_oba && (
                          <Col span={2}>
                            <Tooltip placement="rightTop" title="Uploaded">
                              <CheckOutlined
                                style={{
                                  fontSize: '250%',
                                  color: 'green',
                                }}
                              />
                            </Tooltip>
                          </Col>
                        )}
                      </Row>
                    </Upload>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item name="light_gray_lab" label="LAB">
                    <Upload
                      name="file"
                      fileList={filesState.light_gray_lab}
                      accept={config.FileType.txtType.toString()}
                      beforeUpload={file => {
                        return beforeUpload(file, 'txtType', `${printData.acquisition}.lg.lab`);
                      }}
                      onRemove={e => onRemove(e, 'light_gray_lab')}
                      multiple={false}
                      maxCount={1}
                      onChange={e => fileChange(e, 'light_gray_lab', 'lg.lab.txt')}
                      showUploadList={showFilesList(photoData?.light_gray_lab, 'lg.lab.txt')}
                    >
                      <Row justify="space-between" align="middle">
                        <Col flex="auto">
                          <UploadTrigger fileType="txtType" />
                        </Col>
                        {!!photoData && photoData.light_gray_lab && (
                          <Col span={2}>
                            <Tooltip placement="rightTop" title="Uploaded">
                              <CheckOutlined
                                style={{
                                  fontSize: '250%',
                                  color: 'green',
                                }}
                              />
                            </Tooltip>
                          </Col>
                        )}
                      </Row>
                    </Upload>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item name="light_gray_spectrum" label="Spectrum">
                    <Upload
                      name="file"
                      fileList={filesState.light_gray_spectrum}
                      accept={config.FileType.txtType.toString()}
                      beforeUpload={file => {
                        return beforeUpload(file, 'txtType', `${printData.acquisition}.lg.spectrum`);
                      }}
                      onRemove={e => onRemove(e, 'light_gray_spectrum')}
                      multiple={false}
                      maxCount={1}
                      onChange={e => fileChange(e, 'light_gray_spectrum', 'lg.spectrum.txt')}
                      showUploadList={showFilesList(photoData?.light_gray_spectrum, 'lg.spectrum.txt')}
                    >
                      <Row justify="space-between" align="middle">
                        <Col flex="auto">
                          <UploadTrigger fileType="txtType" />
                        </Col>
                        {photoData?.light_gray_spectrum && (
                          <Col span={2}>
                            <Tooltip placement="rightTop" title="Uploaded">
                              <CheckOutlined
                                style={{
                                  fontSize: '250%',
                                  color: 'green',
                                }}
                              />
                            </Tooltip>
                          </Col>
                        )}
                      </Row>
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>
              <Divider />
              <Row>
                <Col md={24}>
                  <Typography.Title level={4} style={{ margin: 0 }}>
                    Medium Gray
                  </Typography.Title>
                </Col>
              </Row>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item name="medium_gray_oba" label="OBA">
                    <Upload
                      name="file"
                      fileList={filesState.medium_gray_oba}
                      accept={config.FileType.onlyCSV.toString()}
                      beforeUpload={file => {
                        return beforeUpload(file, 'onlyCSV', `${printData.acquisition}.mg.oba`);
                      }}
                      onRemove={e => onRemove(e, 'medium_gray_oba')}
                      multiple={false}
                      maxCount={1}
                      onChange={e => fileChange(e, 'medium_gray_oba', 'mg.oba.csv')}
                      showUploadList={showFilesList(photoData?.medium_gray_oba, 'mg.oba.csv')}
                    >
                      <Row justify="space-between" align="middle">
                        <Col flex="auto">
                          <UploadTrigger fileType="onlyCSV" />
                        </Col>
                        {!!photoData && photoData.medium_gray_oba && (
                          <Col span={2}>
                            <Tooltip placement="rightTop" title="Uploaded">
                              <CheckOutlined
                                style={{
                                  fontSize: '250%',
                                  color: 'green',
                                }}
                              />
                            </Tooltip>
                          </Col>
                        )}
                      </Row>
                    </Upload>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item name="medium_gray_lab" label="LAB">
                    <Upload
                      name="file"
                      fileList={filesState.medium_gray_lab}
                      accept={config.FileType.txtType.toString()}
                      beforeUpload={file => {
                        return beforeUpload(file, 'txtType', `${printData.acquisition}.mg.lab`);
                      }}
                      onRemove={e => onRemove(e, 'medium_gray_lab')}
                      multiple={false}
                      maxCount={1}
                      onChange={e => fileChange(e, 'medium_gray_lab', 'mg.lab.txt')}
                      showUploadList={showFilesList(photoData?.medium_gray_lab, 'mg.lab.txt')}
                    >
                      <Row justify="space-between" align="middle">
                        <Col flex="auto">
                          <UploadTrigger fileType="txtType" />
                        </Col>
                        {!!photoData && photoData.medium_gray_lab && (
                          <Col span={2}>
                            <Tooltip placement="rightTop" title="Uploaded">
                              <CheckOutlined
                                style={{
                                  fontSize: '250%',
                                  color: 'green',
                                }}
                              />
                            </Tooltip>
                          </Col>
                        )}
                      </Row>
                    </Upload>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={8}>
                  <Form.Item name="medium_gray_spectrum" label="Spectrum">
                    <Upload
                      name="file"
                      fileList={filesState.medium_gray_spectrum}
                      accept={config.FileType.txtType.toString()}
                      beforeUpload={file => {
                        return beforeUpload(file, 'txtType', `${printData.acquisition}.mg.spectrum`);
                      }}
                      onRemove={e => onRemove(e, 'medium_gray_spectrum')}
                      multiple={false}
                      maxCount={1}
                      onChange={e => fileChange(e, 'medium_gray_spectrum', 'mg.spectrum.txt')}
                      showUploadList={showFilesList(photoData?.medium_gray_spectrum, 'mg.spectrum.txt')}
                    >
                      <Row justify="space-between" align="middle">
                        <Col flex="auto">
                          <UploadTrigger fileType="txtType" />
                        </Col>
                        {photoData?.medium_gray_spectrum && (
                          <Col span={2}>
                            <Tooltip placement="rightTop" title="Uploaded">
                              <CheckOutlined
                                style={{
                                  fontSize: '250%',
                                  color: 'green',
                                }}
                              />
                            </Tooltip>
                          </Col>
                        )}
                      </Row>
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item {...tailFormItemLayout}>
                <Button
                  type="default"
                  htmlType="button"
                  onClick={backButton}
                  style={{
                    borderColor: '#d9d9d9',
                    background: '#f5f5f5',
                    color: 'rgba(0, 0, 0, 0.85)',
                  }}
                >
                  Back
                </Button>
                {props.showButton()}
                {props.showButton() != '' ? (
                  <Button className="save-btn" type="primary" style={{ margin: '0 8px' }} onClick={() => handleSaveAndNext('next')}>
                    Save and Next
                  </Button>
                ) : (
                  ''
                )}
              </Form.Item>
            </Form>
          </div>
          {/* Tabs End here */}
        </Col>
      </Row>
    </Spin>
  );
};
export default PhotoSpectro;
